// Kampf am Ufer (§6): Geschosse mit Flugzeit (K47, Fabi, 26.10.2026). Ein Geschoss fliegt zu dem Ort, an dem das Ziel
// beim Wurf stand; wer sich bis dahin wegbewegt, weicht aus. Gerade Geschosse treffen den Ersten in ihrer Bahn und
// bleiben an Steinen und am Stamm hängen, Bogenwürfe fliegen über alles hinweg und treffen, was am Landepunkt steht,
// Heilung kommt immer an. Die Geschosse der Freunde fliegen an Freunden vorbei; die der Sturmwesen treffen jeden.
import type { Shot, ShotPayload, ShotStyle, Unit } from '../types.ts';
import type { Battle } from './core.ts';
import { EPS, dist, emit, heal, landHit, round, setStatus, targetable, unitById } from './core.ts';
import { blockerOn, coverOf, inBush, solidAt } from './terrain.ts';

/** Wurfbild eines normalen Angriffs (combat.json, projectiles.attacks), null für Nahkämpfer */
export function rangedArt(b: Battle, u: Unit, tgt: Unit): string | null {
  if (u.range < 10 || u.hideMelee) return null;
  const p = b.rules.projectiles;
  const art = p.attacks[`${u.defId}:${u.friendClass ?? ''}`] ?? p.attacks[u.defId] ?? null;
  // G26: Wer mal wirft, mal zuschlägt, wirft nur, wenn das Ziel weiter weg ist
  if (art && u.hide && dist(u, tgt) - u.radius - tgt.radius <= 6) return null;
  return art;
}

const STRAIGHT = new Set(['gerade', 'gerade, schnell']);

function styleOf(b: Battle, art: string, payload: ShotPayload): ShotStyle {
  if (payload.kind === 'heal') return 'homing';
  return STRAIGHT.has(b.rules.projectiles.flight[art] ?? 'Bogen') ? 'straight' : 'arc';
}

/** Ein Geschoss auf den Weg bringen; es zielt auf den Ort, an dem das Ziel jetzt steht */
export function launch(b: Battle, u: Unit, tgt: Unit, payload: ShotPayload, art: string): Shot {
  const p = b.rules.projectiles;
  const style = styleOf(b, art, payload);
  const speed = p.speed[p.flight[art] ?? 'Bogen'] ?? 70;
  const d = Math.max(EPS, dist(u, tgt));
  const shot: Shot = {
    id: b.nextShotId++,
    from: u.uid,
    side: u.side,
    target: tgt.uid,
    x: u.x,
    y: u.y,
    sx: u.x,
    sy: u.y,
    tx: tgt.x,
    ty: tgt.y,
    speed,
    style,
    t0: b.t,
    t1: b.t + d / speed,
    art,
    payload,
    ignore: coverOf(b.setup.obstacles, u, p.coverReach),
  };
  b.shots.push(shot);
  emit(b, { t: round(b.t), type: 'shot', id: shot.id, from: u.uid, to: tgt.uid, x: round(u.x), y: round(u.y), tx: round(tgt.x), ty: round(tgt.y), style, art, arrive: round(shot.t1) });
  return shot;
}

/** Ein normaler Fernangriff als Geschoss (aus attack) */
export function launchHit(b: Battle, u: Unit, tgt: Unit, raw: number, art: string): void {
  launch(b, u, tgt, { kind: 'hit', raw }, art);
}

/** Wurfbild eines Kniffs mit eigenem Geschoss (Schlammspritzer, Waschwasser) */
export const kniffArt = (b: Battle, kniff: string): string | null => b.rules.projectiles.kniffe[kniff] ?? null;

/** Wen ein Geschoss treffen kann: die Freunde schießen an Freunden vorbei, die Sturmwesen treffen jeden außer sich selbst */
function canHit(b: Battle, s: Shot, o: Unit, straight: boolean): boolean {
  if (o.uid === s.from || !targetable(b, o)) return false;
  if (s.side === 'friend' && o.side === 'friend') return false;
  // K49: Wer im Gebüsch steht, wird von geraden Geschossen nicht getroffen
  if (straight && o.kind !== 'tree' && inBush(b.setup.obstacles, o)) return false;
  return true;
}

function finish(b: Battle, s: Shot, x: number, y: number, hit: Unit | null, obstacle: string | null): void {
  emit(b, { t: round(b.t), type: 'shotEnd', id: s.id, x: round(x), y: round(y), unit: hit?.uid ?? null, obstacle });
  if (hit) apply(b, s, hit);
}

function apply(b: Battle, s: Shot, o: Unit): void {
  const src = b.units[s.from]!;
  const pl = s.payload;
  if (pl.kind === 'hit') landHit(b, src, o, pl.raw);
  else if (pl.kind === 'heal') heal(b, src, o, pl.amount, pl.via);
  else if (pl.kind === 'slow') {
    o.slowUntil = b.t + pl.duration;
    o.slowFactor = pl.slow;
    setStatus(b, o, 'slowed', o.slowUntil, pl.via);
  }
}

/** Ein Takt für alle Geschosse */
export function updateShots(b: Battle): void {
  if (!b.shots.length) return;
  const tick = b.rules.tickSeconds;
  const p = b.rules.projectiles;
  const keep: Shot[] = [];
  for (const s of b.shots) {
    if (s.style === 'homing') {
      const tgt = unitById(b, s.target);
      if (!tgt || !tgt.alive) {
        finish(b, s, s.x, s.y, null, null);
        continue;
      }
      const d = dist(s, tgt);
      const step = s.speed * tick;
      if (d <= step + tgt.radius || b.t - s.t0 > 3) {
        s.x = tgt.x;
        s.y = tgt.y;
        finish(b, s, s.x, s.y, tgt, null);
        continue;
      }
      s.x += ((tgt.x - s.x) / d) * step;
      s.y += ((tgt.y - s.y) / d) * step;
      keep.push(s);
      continue;
    }
    if (s.style === 'arc') {
      if (b.t + EPS < s.t1) {
        const k = (b.t - s.t0) / Math.max(EPS, s.t1 - s.t0);
        s.x = s.sx + (s.tx - s.sx) * k;
        s.y = s.sy + (s.ty - s.sy) * k;
        keep.push(s);
        continue;
      }
      // Bogenwurf: trifft, was am Landepunkt steht
      s.x = s.tx;
      s.y = s.ty;
      let best: Unit | null = null;
      let bestD = Infinity;
      for (const o of b.units) {
        if (!canHit(b, s, o, false)) continue;
        const d = dist(o, s);
        if (d <= o.radius + p.landReach + EPS && d < bestD) {
          best = o;
          bestD = d;
        }
      }
      finish(b, s, s.x, s.y, best, null);
      continue;
    }
    // Gerade: in kleinen Schritten, damit niemand übersprungen wird
    const tgt = b.units[s.target];
    const total = Math.hypot(s.tx - s.sx, s.ty - s.sy);
    const dx = total > EPS ? (s.tx - s.sx) / total : 1;
    const dy = total > EPS ? (s.ty - s.sy) / total : 0;
    const flown = Math.hypot(s.x - s.sx, s.y - s.sy);
    const limit = total + p.overshoot + (tgt?.radius ?? 0);
    const step = Math.min(s.speed * tick, limit - flown);
    const n = Math.max(1, Math.ceil(step));
    let done = false;
    for (let i = 1; i <= n && !done; i++) {
      const x = s.x + (dx * step * i) / n;
      const y = s.y + (dy * step * i) / n;
      const wall = solidAt(b.setup.obstacles, { x, y }, s.ignore);
      if (wall) {
        finish(b, s, x, y, null, wall.id);
        done = true;
        break;
      }
      let best: Unit | null = null;
      let bestD = Infinity;
      for (const o of b.units) {
        if (!canHit(b, s, o, true)) continue;
        const d = Math.hypot(o.x - x, o.y - y);
        if (d <= o.radius + 0.4 && d < bestD) {
          best = o;
          bestD = d;
        }
      }
      if (best) {
        finish(b, s, x, y, best, null);
        done = true;
      }
    }
    if (done) continue;
    s.x += dx * step;
    s.y += dy * step;
    if (flown + step >= limit - EPS) {
      finish(b, s, s.x, s.y, null, null);
      continue;
    }
    keep.push(s);
  }
  b.shots = keep;
}

/** Fernkämpfer zielen nicht auf Versteckte im Gebüsch und lieber auf jemanden mit freier Bahn (K49) */
export function clearShot(b: Battle, u: Unit, o: Unit): boolean {
  if (inBush(b.setup.obstacles, o)) return false;
  return !blockerOn(b.setup.obstacles, u, o, coverOf(b.setup.obstacles, u, b.rules.projectiles.coverReach));
}
